/**
 * Settings → Integrations. Per-provider cards. A provider with no
 * credential fields (Adapty) connects with one click; Adapty then takes a
 * webhook URL per app (AdaptyAppUrlsCard). The design's other providers
 * (Conversion, CatchUp) render as "Coming soon" placeholders.
 *
 * Clicking "Review Event Mapping" opens the existing event-mapping
 * page in-place via the URL — keeps deep-link compatibility.
 */

import { useState } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { GcsConfigModal } from "@/components/GcsConfigModal";
import { IntegrationConfigModal } from "@/components/IntegrationConfigModal";
import {
  useAllAppGcsCredentials,
  useCreateIntegration,
  useDeleteIntegration,
  useIntegrations,
  useProviderCatalogue,
} from "@/lib/api-hooks";
import type { IntegrationSummary, ProviderDescriptor } from "@/lib/types";
import { describeApiError } from "@/lib/api-error";
import { useOrgPath } from "@/lib/useOrgNav";
import { AdaptyAppUrlsCard, AdaptyAppsSummary } from "./AdaptyAppUrlsCard";
import { StConfirmModal, StSectionHead, StStatusDot } from "./_shared";

type ProviderTile = {
  code: string;
  name: string;
  initials: string;
  color: string;
  supported: boolean;
};

const DESIGN_PROVIDERS: ProviderTile[] = [
  { code: "adapty", name: "Adapty", initials: "A", color: "#ff4f6d", supported: true },
  { code: "conversion", name: "Conversion", initials: "Cv", color: "#5b6cff", supported: false },
  { code: "revenuecat", name: "RevenueCat", initials: "Rc", color: "#ff7b3a", supported: true },
  { code: "catchup", name: "CatchUp", initials: "Cu", color: "#0fa37f", supported: false },
];

export function IntegrationsTab() {
  const cat = useProviderCatalogue();
  const mine = useIntegrations();

  const supportedCodes = new Set(
    (cat.data ?? [])
      .filter((p) => p.is_active)
      .map((p) => p.code),
  );
  const tiles = DESIGN_PROVIDERS.map((tile) => ({
    ...tile,
    // Mark supported if backend says it's active. Anything in the
    // design that's not in the backend's catalogue stays "Coming soon".
    supported: tile.supported && supportedCodes.has(tile.code),
  }));

  const byCode = new Map(
    (mine.data ?? []).map((i) => [i.provider_code, i]),
  );

  return (
    <>
      <StSectionHead
        title="Integrations"
        desc="Connect a subscription-events provider (Adapty and friends). Events arrive as webhooks on a URL unique to this org — for Adapty, one per app; canonical event mapping is reviewed from the link on each connected card."
      />
      {(cat.isLoading || mine.isLoading) && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Loading providers…
        </div>
      )}
      {!cat.isLoading && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
          }}
        >
          {tiles.map((p) => (
            <ProviderCard
              key={p.code}
              tile={p}
              integration={byCode.get(p.code) ?? null}
              descriptor={cat.data?.find((d) => d.code === p.code) ?? null}
            />
          ))}
          <GoogleCloudCard />
        </div>
      )}
      {!cat.isLoading && byCode.get("adapty") && <AdaptyAppUrlsCard />}
    </>
  );
}

/** Google Cloud Storage tile — not a webhook subscription provider.
 * Adjust delivers cohort-event CSVs to a GCS bucket; clicking Connect
 * opens the per-app credential modal. */
function GoogleCloudCard() {
  const [open, setOpen] = useState(false);
  const all = useAllAppGcsCredentials();
  const connectedCount = (all.data ?? []).filter((c) => c.enabled).length;
  const connected = connectedCount > 0;
  return (
    <div className="card card-pad">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: "#4285f4",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          GC
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Google Cloud</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            GCS bucket · Adjust cohort events
          </div>
        </div>
        {connected ? (
          <StStatusDot kind="ok" />
        ) : (
          <span className="badge">
            <span className="dot" style={{ background: "var(--c-text-4)" }} />
            Off
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
        {connected
          ? `Connected · ${connectedCount} app${connectedCount === 1 ? "" : "s"} ingesting Adjust cohort events.`
          : "Pull Adjust cohort-event CSVs from a Google Cloud Storage bucket. Configured per app."}
      </div>
      <Btn variant={connected ? "default" : "primary"} onClick={() => setOpen(true)}>
        {connected ? "Manage" : "Configure"}
      </Btn>
      {open && <GcsConfigModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function ProviderCard({
  tile,
  integration,
  descriptor,
}: {
  tile: ProviderTile;
  integration: IntegrationSummary | null;
  descriptor: ProviderDescriptor | null;
}) {
  const orgPath = useOrgPath();
  const del = useDeleteIntegration();
  const create = useCreateIntegration();
  const isAdapty = tile.code === "adapty";
  const [confirmOff, setConfirmOff] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  // Providers with `credentials_fields` need explicit user input —
  // open the config modal on Connect. Adapty has zero fields and
  // auto-provisions on the first /api/integrations read, so the
  // "Provisioning…" placeholder stays as-is for it.
  const needsManualConfig =
    !!descriptor && descriptor.credentials_fields.length > 0;

  const connected = integration?.is_connected ?? false;
  const url = integration?.webhook_url ?? "";

  const onCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="card card-pad">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: tile.color,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {tile.initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{tile.name}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {tile.supported
              ? "Webhook · subscription events"
              : "Coming in v1.x"}
          </div>
        </div>
        {connected ? (
          <StStatusDot kind="ok" />
        ) : (
          <span className="badge">
            <span
              className="dot"
              style={{ background: "var(--c-text-4)" }}
            />
            Off
          </span>
        )}
      </div>

      {tile.supported && integration && (url || isAdapty) && (
        <>
          {url && (
            <div className="field" style={{ marginBottom: 10 }}>
              <label className="label">
                {isAdapty ? "Shared webhook URL (old)" : "Webhook URL"}
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="input mono"
                  value={url}
                  readOnly
                  style={{ flex: 1, fontSize: 12 }}
                />
                <Btn icon={I.copy} onClick={() => void onCopy()}>
                  {copied ? "Copied!" : "Copy"}
                </Btn>
              </div>
              {isAdapty && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
                  Still accepted for apps set up on it, but its events can&apos;t be told apart by
                  app. Give each app its own URL below.
                </div>
              )}
            </div>
          )}
          {isAdapty && <AdaptyAppsSummary />}
          {integration.last_webhook_at && (
            <div
              style={{
                fontSize: 12,
                color: "var(--c-text-3)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              {I.clock}
              <span>
                Last data received:{" "}
                {new Date(integration.last_webhook_at).toLocaleString()}
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "space-between",
            }}
          >
            <a
              href={orgPath(
                `/settings/event-mapping/${integration.id}`,
              )}
              className="btn"
            >
              Review Event Mapping
            </a>
            <Btn variant="danger" onClick={() => setConfirmOff(true)}>
              Disconnect
            </Btn>
          </div>
        </>
      )}
      {tile.supported && !integration && !needsManualConfig && (
        <>
          <Btn
            variant="primary"
            disabled={create.isPending}
            onClick={() =>
              create.mutate({
                provider_code: tile.code,
                config: {},
                enabled_modes: descriptor?.supported_modes ?? ["webhook"],
              })
            }
          >
            {create.isPending ? "Connecting…" : "Connect"}
          </Btn>
          {create.isError && (
            <div style={{ fontSize: 12, marginTop: 8, color: "#c5221f" }}>
              {describeApiError(create.error, "Couldn't connect.")}
            </div>
          )}
        </>
      )}
      {tile.supported && !integration && needsManualConfig && (
        <Btn variant="primary" onClick={() => setShowConnect(true)}>
          Connect
        </Btn>
      )}
      {!tile.supported && (
        <Btn disabled>Coming soon</Btn>
      )}
      <StConfirmModal
        open={confirmOff}
        title={`Disconnect ${tile.name}?`}
        body="Webhook deliveries will stop. Existing events stay in the database."
        danger
        onCancel={() => setConfirmOff(false)}
        onConfirm={() => {
          if (integration) void del.mutate(integration.id);
          setConfirmOff(false);
        }}
      />
      {showConnect && descriptor && (
        <IntegrationConfigModal
          descriptor={descriptor}
          onClose={() => setShowConnect(false)}
          onConnected={() => setShowConnect(false)}
        />
      )}
    </div>
  );
}
